import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { StarsButton } from '../components/StarsButton';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { useUser } from '../lib/auth';
import { getTelegram, haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

export function LeagueDetail() {
  const { slug } = useParams<{ slug: string }>();
  const user = useUser();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['league', slug],
    queryFn: () => api.leagueDetail(slug!),
    enabled: !!slug,
  });

  const join = useMutation({
    mutationFn: () => api.joinLeague(slug!),
    onSuccess: async () => {
      haptic.success();
      if (q.data) {
        track('league_joined', {
          league_slug: q.data.league.slug,
          league_type: q.data.league.leagueType,
        });
      }
      await qc.invalidateQueries({ queryKey: ['league', slug] });
      await qc.invalidateQueries({ queryKey: ['leagues', 'me'] });
    },
    onError: () => haptic.error(),
  });

  if (q.isLoading) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10">
        <div className="h-32 animate-pulse rounded-xl bg-bg-card border border-border-base" />
      </div>
    );
  }
  if (!q.data) {
    return <div className="mx-auto max-w-md px-4 pt-10 text-text-dim">League not found.</div>;
  }

  const isMember = q.data.leaderboard.some((r) => r.userId === user.data?.id);
  const myRow = q.data.leaderboard.find((r) => r.userId === user.data?.id);
  const isOwner = q.data.league.ownerUserId === user.data?.id;
  const sponsored = q.data.league.isSponsored === 1;

  function share() {
    track('share_clicked', { surface: 'league' });
    const tg = getTelegram();
    const startLink = `https://t.me/PunditXIBot?start=league_${slug}`;
    const text = `Join my PunditXI league "${q.data!.league.name}".`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(startLink)}&text=${encodeURIComponent(text)}`;
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, '_blank');
  }

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header>
        <div className="flex items-center gap-2">
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            {q.data.league.leagueType} League
          </div>
          {q.data.league.isSponsored === 1 && (
            <span className="rounded-md bg-premium/20 border border-premium/40 px-2 py-0.5 font-display-alt text-[10px] uppercase tracking-widest text-premium">
              Sponsored
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl uppercase tracking-wide mt-1">{q.data.league.name}</h1>
        {q.data.league.description && (
          <p className="text-text-dim text-sm mt-1">{q.data.league.description}</p>
        )}
        {q.data.league.isSponsored === 1 && q.data.league.sponsorName && (
          <div className="mt-3 rounded-lg border border-premium/30 bg-bg-card p-3 flex items-center gap-3">
            {q.data.league.sponsorLogoUrl && (
              <img
                src={q.data.league.sponsorLogoUrl}
                alt={q.data.league.sponsorName}
                className="h-10 w-10 object-contain"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display-alt text-[10px] uppercase tracking-widest text-text-faint">
                Sponsored by
              </div>
              <div className="font-chunky text-sm truncate">
                {q.data.league.sponsorLinkUrl ? (
                  <a
                    href={q.data.league.sponsorLinkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-premium"
                  >
                    {q.data.league.sponsorName} →
                  </a>
                ) : (
                  q.data.league.sponsorName
                )}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 mt-2 text-sm">
          <span className="font-mono text-text-dim">{q.data.league.memberCount} members</span>
          {myRow && (
            <span className="font-mono text-trophy-gold">
              Rank #{q.data.leaderboard.indexOf(myRow) + 1}
            </span>
          )}
        </div>
      </header>

      {!isMember && (
        <Button variant="gold" fullWidth onClick={() => join.mutate()} disabled={join.isPending}>
          {join.isPending ? 'Joining…' : 'Join league'}
        </Button>
      )}

      {isMember && (
        <Button variant="primary" fullWidth onClick={share}>
          Share link
        </Button>
      )}

      {isOwner && <SponsorPanel slug={slug!} sponsored={sponsored} league={q.data.league} />}

      <section>
        <h2 className="font-display text-xl uppercase tracking-wide mb-2">Leaderboard</h2>
        {q.data.leaderboard.length === 0 ? (
          <Card>
            <div className="text-text-dim text-sm">No members yet.</div>
          </Card>
        ) : (
          <div className="rounded-xl border border-border-base bg-bg-card overflow-hidden">
            {q.data.leaderboard.map((row, i) => {
              const isMe = row.userId === user.data?.id;
              return (
                <div
                  key={row.userId}
                  className={cn(
                    'flex items-center justify-between px-4 py-2 border-b border-border-base last:border-b-0',
                    i < 3 && 'bg-trophy-gold/5',
                    isMe && 'bg-trophy-gold/15',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-text-faint w-6 text-right tabular-nums">{i + 1}</span>
                    <span className={cn('font-chunky text-sm', isMe && 'text-trophy-gold')}>
                      {row.firstName}
                      {isMe && ' (you)'}
                    </span>
                  </div>
                  <span className="font-mono text-trophy-gold">{row.totalPoints}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

interface SponsorPanelProps {
  slug: string;
  sponsored: boolean;
  league: NonNullable<Awaited<ReturnType<typeof api.leagueDetail>>['league']>;
}

function SponsorPanel({ slug, sponsored, league }: SponsorPanelProps) {
  const qc = useQueryClient();
  const [name, setName] = useState(league.sponsorName ?? '');
  const [logoUrl, setLogoUrl] = useState(league.sponsorLogoUrl ?? '');
  const [linkUrl, setLinkUrl] = useState(league.sponsorLinkUrl ?? '');
  const [editing, setEditing] = useState(!league.sponsorName);

  const save = useMutation({
    mutationFn: () =>
      api.sponsorLeague(slug, {
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
        linkUrl: linkUrl.trim() || undefined,
      }),
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['league', slug] });
      setEditing(false);
    },
    onError: () => haptic.error(),
  });

  // Pre-purchase state: show the upgrade CTA.
  if (!sponsored) {
    return (
      <Card className="border-premium/40 space-y-3">
        <div>
          <div className="font-display-alt text-xs uppercase tracking-widest text-premium">
            Owner · upgrade
          </div>
          <div className="font-chunky text-base mt-1">Brand this league</div>
          <ul className="space-y-1 text-xs text-text-dim mt-2">
            <li>• Custom sponsor name, logo, and link on the league page</li>
            <li>• Priority placement in the Public tab</li>
            <li>• Sponsor badge across every member's view</li>
          </ul>
        </div>
        <StarsButton
          productCode="PAID_LEAGUE"
          leagueSlug={slug}
          label="Sponsor this league"
          stars={2000}
          variant="primary"
          fullWidth
          invalidateKeys={[['league', slug]]}
        />
        <div className="text-[10px] text-text-faint leading-relaxed">
          One-off Telegram Stars payment. After it lands, set sponsor details below.
        </div>
      </Card>
    );
  }

  // Post-purchase, no metadata yet OR editing.
  if (editing) {
    return (
      <Card className="border-premium/40 space-y-3">
        <div className="font-display-alt text-xs uppercase tracking-widest text-premium">
          Owner · sponsor details
        </div>
        <label className="block">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Sponsor name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="Local Pub Crew"
            className="mt-1 w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-premium"
          />
        </label>
        <label className="block">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Logo URL (optional)
          </span>
          <input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://..."
            className="mt-1 w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-premium"
          />
        </label>
        <label className="block">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Link URL (optional)
          </span>
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            className="mt-1 w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-premium"
          />
        </label>
        <Button
          variant="primary"
          fullWidth
          disabled={save.isPending || name.trim().length < 1}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save sponsor details'}
        </Button>
        {save.error && (
          <div className="text-crimson text-xs text-center">
            {save.error instanceof Error ? save.error.message : 'Save failed'}
          </div>
        )}
      </Card>
    );
  }

  // Post-purchase, metadata set, not editing — show what's live with an Edit affordance.
  return (
    <Card className="border-premium/40 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="font-display-alt text-xs uppercase tracking-widest text-premium">
          Owner · sponsorship live
        </div>
        <button
          onClick={() => setEditing(true)}
          className="font-display-alt text-[10px] uppercase tracking-widest text-text-dim"
        >
          Edit
        </button>
      </div>
      <div className="text-xs text-text-dim">
        Current sponsor: <span className="font-chunky text-text-base">{league.sponsorName}</span>
      </div>
    </Card>
  );
}
